import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { actions, selectors } from 'farmx-redux-core';
import { Button, Input } from 'antd';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { ImSearch } from 'react-icons/im';
import StatsPage from './StatsPage';
import { columnDefs as defaultColumnDefinitions } from '../columnDefs';
import { BlockViewCellRenderer } from './cellRendererComponents';

const blockColumnDefs = {
  ...defaultColumnDefinitions,
  id: {
    ...defaultColumnDefinitions.id,
    headerName: 'Id',
  },
  ranchId: {
    ...defaultColumnDefinitions.ranchId,
    headerName: 'Ranch Id',
  },
  entityName: {
    ...defaultColumnDefinitions.entityName,
    headerName: 'Entity Name',
    cellClass: 'block-entity-name-cell',
  },
  name: {
    ...defaultColumnDefinitions.name,
    headerName: 'Name',
  },
  areaAcres: {
    ...defaultColumnDefinitions.areaAcres,
    headerName: 'Acres',
  },
  controlEnabled: {
    ...defaultColumnDefinitions.controlEnabled,
    headerName: 'Control Enabled',
  },
  schedulingEnabled: {
    ...defaultColumnDefinitions.schedulingEnabled,
    headerName: 'Scheduling Enabled',
  },
  editBlock: {
    ...defaultColumnDefinitions.editBlock,
    headerName: 'Edit Block',
    cellRenderer: 'blockViewCellRenderer',
  },
};

const blockStatsPageStore = {
  page: 1,
  searchText: '',
  searchInputValue: '',
  pageCache: {},
  activeQueryKey: null,
};

export default function BlockStatsPage() {
  const dispatch = useDispatch();
  const [page, setPage] = useState(blockStatsPageStore.page || 1);
  const [pageSize] = useState(50);
  const [searchText, setSearchText] = useState(blockStatsPageStore.searchText || '');
  const [searchInputValue, setSearchInputValue] = useState(blockStatsPageStore.searchInputValue || '');
  const [pageCache, setPageCache] = useState(blockStatsPageStore.pageCache || {});
  const [activeQueryKey, setActiveQueryKey] = useState(blockStatsPageStore.activeQueryKey || null);
  const pendingQueryKeyRef = useRef(null);
  const wasLoadingRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);

  const buildQueryParams = useCallback((targetPage = page) => {
    const query = {
      page: targetPage,
      page_size: pageSize,
    };
    const trimmedSearch = (searchText || '').trim();
    if (trimmedSearch) {
      query.search = trimmedSearch;
    }
    return query;
  }, [page, pageSize, searchText]);

  const getQueryKey = useCallback((query) => [
    `page=${query.page || 1}`,
    `pageSize=${query.page_size || pageSize}`,
    `search=${query.search || ''}`,
  ].join('|'), [pageSize]);

  const requestBlockStats = useCallback((query, options = {}) => {
    const { forceRefresh = false } = options;
    const queryKey = getQueryKey(query);
    setActiveQueryKey(queryKey);

    if (!forceRefresh && pageCache[queryKey]) {
      pendingQueryKeyRef.current = null;
      return;
    }

    if (forceRefresh) {
      setPageCache((currentCache) => {
        const updatedCache = { ...currentCache };
        delete updatedCache[queryKey];
        return updatedCache;
      });
    }

    pendingQueryKeyRef.current = queryKey;
    dispatch(actions.loadBlockStatsList(query));
  }, [dispatch, getQueryKey, pageCache]);

  const loadData = useCallback(() => {
    requestBlockStats(buildQueryParams(page));
  }, [buildQueryParams, page, requestBlockStats]);

  const refreshData = useCallback(() => {
    requestBlockStats(buildQueryParams(page), { forceRefresh: true });
  }, [buildQueryParams, page, requestBlockStats]);

  const blockStats = useSelector((state) => selectors.selectAllBlockStats(state));
  const blockStatsLoading = useSelector((state) => selectors.selectBlockStatsState(state));
  const {
    loading,
    lastUpdated,
    count = 0,
    next = null,
    previous = null,
  } = blockStatsLoading;

  useEffect(() => {
    blockStatsPageStore.page = page;
    blockStatsPageStore.searchText = searchText;
    blockStatsPageStore.searchInputValue = searchInputValue;
    blockStatsPageStore.pageCache = pageCache;
    blockStatsPageStore.activeQueryKey = activeQueryKey;
  }, [page, searchText, searchInputValue, pageCache, activeQueryKey]);

  useEffect(() => {
    const requestJustFinished = wasLoadingRef.current && !loading;
    wasLoadingRef.current = loading;

    if (!requestJustFinished || !pendingQueryKeyRef.current) return;

    const queryKey = pendingQueryKeyRef.current;
    setPageCache((currentCache) => ({
      ...currentCache,
      [queryKey]: {
        data: blockStats,
        count,
        next,
        previous,
        lastUpdated,
      },
    }));
    pendingQueryKeyRef.current = null;
  }, [loading, blockStats, count, next, previous, lastUpdated]);

  useEffect(() => {
    if (!loading && lastUpdated) {
      hasLoadedOnceRef.current = true;
    }
  }, [loading, lastUpdated]);

  const activeCachedResponse = activeQueryKey ? pageCache[activeQueryKey] : null;
  const displayedData = activeCachedResponse?.data || (activeQueryKey ? [] : blockStats);
  const displayedCount = activeCachedResponse?.count ?? count;
  const displayedLastUpdated = activeCachedResponse?.lastUpdated ?? lastUpdated;
  const displayedLoading = (loading && !activeCachedResponse)
    || (!hasLoadedOnceRef.current && !activeCachedResponse);
  const totalPages = Math.max(1, Math.ceil((displayedCount || 0) / pageSize));
  const hasPreviousPage = page > 1;
  const hasNextPage = page < totalPages;

  const goToPage = useCallback((nextPage) => {
    const targetPage = Math.min(Math.max(1, nextPage), totalPages);
    setPage(targetPage);
    requestBlockStats(buildQueryParams(targetPage));
  }, [buildQueryParams, requestBlockStats, totalPages]);

  const submitSearch = useCallback((rawValue = searchInputValue) => {
    const value = rawValue || '';
    const trimmedValue = value.trim();

    setSearchInputValue(value);
    setSearchText(value);
    setPage(1);

    const query = {
      page: 1,
      page_size: pageSize,
    };
    if (trimmedValue) {
      query.search = trimmedValue;
    }
    requestBlockStats(query);
  }, [pageSize, requestBlockStats, searchInputValue]);

  const onGlobalSearchChange = (event) => {
    const value = event?.target?.value || '';
    setSearchInputValue(value);

    if (value === '' && searchText) {
      submitSearch('');
    }
  };

  return (
    <StatsPage
      title="Block Stats"
      loadData={loadData}
      refreshData={refreshData}
      data={displayedData}
      loading={displayedLoading}
      lastUpdated={displayedLastUpdated}
      sort={null}
      columns={[
        'id',
        'ranchId',
        'entityName',
        'name',
        'areaAcres',
        'controlEnabled',
        'schedulingEnabled',
        'editBlock',
      ]}
      columnDefinitionsOverride={blockColumnDefs}
      frameworkComponents={{
        blockViewCellRenderer: BlockViewCellRenderer,
      }}
      headerContent={(
        <span className="header-btns ranch-stats-toolbar">
          <span className="ranch-toolbar-actions">
            <Input
              className="ranch-search-input"
              placeholder="Search"
              value={searchInputValue}
              onChange={onGlobalSearchChange}
              onPressEnter={() => submitSearch(searchInputValue)}
              suffix={(
                <Button
                  type="link"
                  size="small"
                  className="ranch-search-submit-button"
                  onClick={() => submitSearch(searchInputValue)}
                  aria-label="Search block statistics"
                >
                  <ImSearch className="ranch-search-icon" />
                </Button>
              )}
              allowClear
            />
            <Button
              onClick={() => goToPage(page - 1)}
              disabled={!hasPreviousPage || displayedLoading}
            >
              <FaChevronLeft />
            </Button>
            <Button disabled>
              Page
              {' '}
              {page}
            </Button>
            <Button
              onClick={() => goToPage(page + 1)}
              disabled={!hasNextPage || displayedLoading}
            >
              <FaChevronRight />
            </Button>
            <Button disabled>
              Total
              {' '}
              {displayedCount}
            </Button>
          </span>
        </span>
      )}
    />
  );
}
